import React, { useState } from 'react';
import Moon from './Moon';

interface SkillCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  technologies: string[];
  position: { x: number; y: number; z: number };
  planetMode: {
    radius: number;
    orbitRadius: number;
    rotationSpeed: number;
    texture: string;
    atmosphere: {
      color: string;
      intensity: number;
    };
    moons: Array<{
      name: string;
      orbitRadius: number;
      size: number;
      speed: number;
    }>;
  };
  color: string;
  level: number;
  influences: string[];
  dependencies: string[];
  connections: Array<{
    targetId: string;
    strength: number;
    type: 'influence' | 'dependency' | 'synergy' | 'orbit';
  }>;
}

interface PlanetProps {
  skill: SkillCategory;
  className?: string;
  onClick: () => void;
  isFocused: boolean;
  onMoonHover?: (moonName: string) => void;
  onMoonClick?: (moonName: string) => void;
}

const Planet: React.FC<PlanetProps> = ({ 
  skill, 
  className = '', 
  onClick, 
  isFocused, 
  onMoonHover, 
  onMoonClick 
}) => {
  const [hoveredMoon, setHoveredMoon] = useState<string | null>(null);

  const planetStyle = {
    '--planet-color': skill.color,
    '--atmosphere-color': skill.planetMode.atmosphere.color,
    '--atmosphere-intensity': skill.planetMode.atmosphere.intensity,
    '--rotation-speed': `${skill.planetMode.rotationSpeed}s`
  } as React.CSSProperties;

  const handleMoonHover = (moonName: string) => {
    setHoveredMoon(moonName);
    if (onMoonHover) {
      onMoonHover(moonName);
    }
  };

  const handleMoonLeave = () => {
    setHoveredMoon(null);
  };

  const handleMoonClick = (moonName: string) => {
    if (onMoonClick) {
      onMoonClick(moonName);
    }
  };

  const isSoloFocus = className.includes('planet--solo-focus');

  return (
    <div
      className={`planet depth-interactive ${className} ${isFocused ? 'planet--focused' : ''} ${isSoloFocus ? 'planet--solo-focus' : ''}`}
      style={planetStyle}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`${skill.name} skill planet - ${skill.description}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Planet Surface */}
      <div className="planet__surface">
        {/* Planet Texture */}
        <div className="planet__texture"></div>
        
        {/* Planet Icon */}
        <div className="planet__icon">{skill.icon}</div>
      </div>

      {/* Planet Atmosphere */}
      <div className="planet__atmosphere planet-layer-planet-atmosphere"></div>

      {/* Planet Glow */}
      <div className="planet__glow planet-layer-planet-glow"></div>

      {/* Moon System (positioned around planet when in solo focus mode) */}
      {isFocused && (
        <div className={`planet__moon-system depth-layer-moons ${isSoloFocus ? 'moon-system--floating' : ''}`}>
          {skill.planetMode.moons.map((moon, index) => (
            <Moon
              key={moon.name}
              moon={moon}
              index={index}
              planetColor={skill.color}
              isVisible={isFocused}
              isSoloFocus={isSoloFocus}
              onHover={handleMoonHover}
              onLeave={handleMoonLeave}
              onClick={handleMoonClick}
            />
          ))}
        </div>
      )}

      {/* Hovered Moon Information (only show when not in solo focus mode) */}
      {hoveredMoon && !isSoloFocus && (
        <div className="planet__moon-info">
          <div className="moon-info__title">Technology: {hoveredMoon}</div>
          <div className="moon-info__description">
            Core technology in the {skill.name} ecosystem
          </div>
        </div>
      )}
    </div>
  );
};

export default Planet; 